import { motion } from "framer-motion";
import {
  FieldWidth,
  FormSection as FormSectionType,
  isFieldRequired,
  isFieldVisible,
  resolveFieldWidth,
  resolveFormRequirements,
} from "@aam/shared";
import { useFormStore } from "../store/formStore";
import { FormFieldInput } from "./FormFieldInput";
import { CarrierStepPreview } from "./CarrierStepPreview";
import { ReviewSummary } from "./ReviewSummary";
import { AutofillBanner } from "./AutofillBanner";

interface FormSectionProps {
  section: FormSectionType;
}

function SigningDisclosure({ variant }: { variant: "w9" | "eft" | "documents" | "review" }) {
  if (variant === "documents") {
    return (
      <div className="rounded-card border-2 border-border-default bg-surface-overlay p-5">
        <p className="text-xs uppercase tracking-[0.2em] text-content-muted mb-2">Documents you will sign</p>
        <ul className="space-y-1.5 text-sm text-content-secondary">
          <li>• <strong className="text-content-primary">IRS Form W9</strong> — tax certification (this step)</li>
          <li>• <strong className="text-content-primary">EFT authorization</strong> — direct deposit consent (banking step)</li>
          <li>• <strong className="text-content-primary">Commission offset acknowledgment</strong> — final review step</li>
        </ul>
      </div>
    );
  }

  if (variant === "w9") {
    return (
      <div className="rounded-card border-2 border-accent bg-accent-muted p-5">
        <p className="text-xs uppercase tracking-[0.2em] text-accent mb-2">W9 signing disclosure</p>
        <p className="text-sm text-content-primary leading-relaxed">
          You are signing IRS Form W9. Your signature certifies that the tax information above is correct
          under penalty of perjury. A completed PDF will be generated and stored with your contract request.
        </p>
      </div>
    );
  }

  if (variant === "eft") {
    return (
      <div className="rounded-card border-2 border-accent bg-accent-muted p-5">
        <p className="text-xs uppercase tracking-[0.2em] text-accent mb-2">EFT signing disclosure</p>
        <p className="text-sm text-content-primary leading-relaxed">
          You are signing an Electronic Funds Transfer authorization. Your signature authorizes commission
          payments to the bank account listed above.
        </p>
      </div>
    );
  }

  return null;
}

function ReviewChecklist() {
  const formData = useFormStore((s) => s.formData);
  const w9Signed = Boolean(formData.w9Signature);
  const eftSigned = Boolean(formData.eftSignature);

  return (
    <div className="rounded-card border-2 border-border-default bg-surface-overlay p-5 space-y-2">
      <p className="text-xs uppercase tracking-[0.2em] text-content-muted mb-2">Documents signed</p>
      <div className="flex items-center gap-2 text-sm">
        <span className={w9Signed ? "text-status-active-text" : "text-content-muted"}>{w9Signed ? "✓" : "○"}</span>
        <span className={w9Signed ? "text-content-primary" : "text-content-muted"}>W9 tax certification</span>
      </div>
      <div className="flex items-center gap-2 text-sm">
        <span className={eftSigned ? "text-status-active-text" : "text-content-muted"}>{eftSigned ? "✓" : "○"}</span>
        <span className={eftSigned ? "text-content-primary" : "text-content-muted"}>EFT authorization</span>
      </div>
      <p className="text-xs text-content-muted pt-1">Complete the commission offset acknowledgment below to finish.</p>
    </div>
  );
}

function shouldShowPinnacleReviewChecklist(selectedCarriers: string[]) {
  return resolveFormRequirements(selectedCarriers).needsPinnacle;
}

/**
 * Fields sit on a 12-column grid that stays single-column until `md`. Widths come
 * from the schema (see `resolveFieldWidth`) instead of `:has()` guesses about the
 * rendered DOM, and the grid is `items-start` so a tall neighbour — the licensed
 * states picker, say — no longer stretches a two-option radio card to match it.
 */
const FIELD_WIDTH_CLASS: Record<FieldWidth, string> = {
  third: "col-span-12 md:col-span-6 lg:col-span-4",
  half: "col-span-12 md:col-span-6",
  full: "col-span-12",
};

const SECTION_GRID = "grid grid-cols-12 items-start gap-5";

export function FormSection({ section }: FormSectionProps) {
  const formData = useFormStore((s) => s.formData);
  const selectedCarriers = (formData.selectedCarriers as string[]) ?? [];
  const visibleFields = section.fields
    .filter((field) => field.id !== "reviewSummary")
    .filter((field) => isFieldVisible(field, formData))
    .map((field) => ({ ...field, required: isFieldRequired(field, formData) }));

  const signingFieldIds = new Set(["w9SignatureAcknowledgment", "w9Signature", "eftSignatureAcknowledgment", "eftSignature"]);
  const fieldsBeforeSigning = visibleFields.filter((f) => !signingFieldIds.has(f.id));
  const signingFields = visibleFields.filter((f) => signingFieldIds.has(f.id));

  const fieldList = (
    <>
      <AutofillBanner fieldIds={visibleFields.map((field) => field.id)} />
      {fieldsBeforeSigning.map((field, idx) => {
        return (
          <motion.div
            key={field.id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: Math.min(idx * 0.03, 0.12), duration: 0.2 }}
            className={`${FIELD_WIDTH_CLASS[resolveFieldWidth(field)]} rounded-card border-2 border-border-default bg-surface-raised p-5 shadow-card sm:p-6`}
          >
            <FormFieldInput field={field} />
          </motion.div>
        );
      })}

      {section.id === "tax_information" && signingFields.length > 0 && (
        <div className="col-span-12">
          <SigningDisclosure variant="w9" />
        </div>
      )}
      {section.id === "banking_information" && signingFields.length > 0 && (
        <div className="col-span-12">
          <SigningDisclosure variant="eft" />
        </div>
      )}
      {section.id === "application_review" && (
        <>
          <div className="col-span-12">
            <ReviewSummary />
          </div>
          {shouldShowPinnacleReviewChecklist(selectedCarriers) && (
            <div className="col-span-12">
              <ReviewChecklist />
            </div>
          )}
        </>
      )}

      {signingFields.map((field, idx) => (
        <motion.div
          key={field.id}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: Math.min((fieldsBeforeSigning.length + idx) * 0.03, 0.12), duration: 0.2 }}
          className={`${FIELD_WIDTH_CLASS[resolveFieldWidth(field)]} rounded-card border-2 border-accent bg-accent-muted p-5 shadow-card sm:p-6`}
        >
          <FormFieldInput field={field} />
        </motion.div>
      ))}
    </>
  );

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      <div className="mb-8 border-b-2 border-border-default pb-6">
        <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-accent">Contract request</p>
        <h2 id="application-step-heading" className="mt-2 font-display text-4xl font-extrabold leading-none tracking-[-0.045em] text-content-primary sm:text-5xl">{section.title}</h2>
        {section.description && (
          <p className="mt-4 max-w-3xl text-base leading-7 text-content-secondary">{section.description}</p>
        )}
      </div>

      {section.id === "carriers_to_contract" ? (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start">
          <div className={SECTION_GRID}>{fieldList}</div>
          <CarrierStepPreview carriers={selectedCarriers} />
        </div>
      ) : (
        <div className={SECTION_GRID}>
          {section.id === "tax_information" && (
            <div className="col-span-12">
              <SigningDisclosure variant="documents" />
            </div>
          )}
          {fieldList}
        </div>
      )}
    </motion.div>
  );
}
